import type {
  GetClientActiveAnnouncement,
  GetLimeAdminAnnouncementById,
  GetLimeAdminAnnouncementImageUploadUrl,
  GetLimeAdminAnnouncementOptions,
  GetLimeAdminAnnouncements,
  PostClientAnnouncementClosed,
  PostClientAnnouncementShown,
  PostLimeAdminAnnouncement,
  PostLimeAdminAnnouncementImageComplete,
  PutLimeAdminAnnouncementById,
  PutLimeAdminAnnouncementStatus,
} from "@/server-types";
import { queryClient } from "..";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { get, post, put } from "../request-helpers";

const ACTIVE_ANNOUNCEMENT_QUERY_KEY = ["activeAnnouncement"];
const LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY = ["limeAdminAnnouncements"];

function limeAdminAnnouncementQueryKey(announcementId: number) {
  return [...LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY, announcementId];
}

/**
 * GET /client/announcements/active
 */
export const useGetActiveAnnouncement = (options: { enabled: boolean }) => {
  return useCustomQuery({
    queryKey: ACTIVE_ANNOUNCEMENT_QUERY_KEY,
    queryFn: () =>
      get<GetClientActiveAnnouncement>("/client/announcements/active"),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: options.enabled,
  });
};

/**
 * POST /client/announcements/:announcementId/shown
 */
export const usePostAnnouncementShown = () => {
  return useCustomMutation({
    mutationFn: (announcementId: number) =>
      post<PostClientAnnouncementShown>(
        `/client/announcements/${announcementId}/shown`,
        {},
      ),
    // The endpoint is an idempotent upsert and the modal posts it only once.
    retry: 2,
  });
};

/**
 * POST /client/announcements/:announcementId/closed
 */
export const usePostAnnouncementClosed = () => {
  return useCustomMutation({
    mutationFn: ({
      announcementId,
      ...body
    }: PostClientAnnouncementClosed["body"] & { announcementId: number }) =>
      post<PostClientAnnouncementClosed>(
        `/client/announcements/${announcementId}/closed`,
        body,
      ),
    // The cached announcement never goes stale, so without this a remounted dashboard would show it again.
    onMutate: () => {
      queryClient.setQueryData<GetClientActiveAnnouncement["response"]>(
        ACTIVE_ANNOUNCEMENT_QUERY_KEY,
        { announcement: null },
      );
    },
  });
};

/**
 * GET /lime-admin/announcements
 */
export const useGetLimeAdminAnnouncements = () => {
  return useCustomQuery({
    queryKey: LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY,
    queryFn: () => get<GetLimeAdminAnnouncements>("/lime-admin/announcements"),
  });
};

/**
 * GET /lime-admin/announcements/options
 */
export const useGetLimeAdminAnnouncementOptions = () => {
  return useCustomQuery({
    queryKey: ["limeAdminAnnouncementOptions"],
    queryFn: () =>
      get<GetLimeAdminAnnouncementOptions>("/lime-admin/announcements/options"),
    staleTime: Infinity,
  });
};

/**
 * GET /lime-admin/announcements/:announcementId
 */
export const useGetLimeAdminAnnouncement = (announcementId: number) => {
  return useCustomQuery({
    queryKey: limeAdminAnnouncementQueryKey(announcementId),
    queryFn: () =>
      get<GetLimeAdminAnnouncementById>(
        `/lime-admin/announcements/${announcementId}`,
      ),
  });
};

/**
 * POST /lime-admin/announcements
 */
export const usePostLimeAdminAnnouncement = (options?: {
  onSuccess?: (data: PostLimeAdminAnnouncement["response"]) => void;
}) => {
  return useCustomMutation({
    mutationFn: (body: PostLimeAdminAnnouncement["body"]) =>
      post<PostLimeAdminAnnouncement>("/lime-admin/announcements", body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY,
      });
      options?.onSuccess?.(data);
    },
  });
};

/**
 * PUT /lime-admin/announcements/:announcementId
 */
export const usePutLimeAdminAnnouncement = (
  announcementId: number,
  options?: {
    onSuccess?: (data: PutLimeAdminAnnouncementById["response"]) => void;
  },
) => {
  return useCustomMutation({
    mutationFn: (body: PutLimeAdminAnnouncementById["body"]) =>
      put<PutLimeAdminAnnouncementById>(
        `/lime-admin/announcements/${announcementId}`,
        body,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(
        limeAdminAnnouncementQueryKey(announcementId),
        data,
      );
      queryClient.invalidateQueries({
        queryKey: LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY,
        exact: true,
      });
      options?.onSuccess?.(data);
    },
    displayNotification: true,
  });
};

/**
 * PUT /lime-admin/announcements/:announcementId/status
 */
export const usePutLimeAdminAnnouncementStatus = (announcementId: number) => {
  return useCustomMutation({
    mutationFn: (body: PutLimeAdminAnnouncementStatus["body"]) =>
      put<PutLimeAdminAnnouncementStatus>(
        `/lime-admin/announcements/${announcementId}/status`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: LIME_ADMIN_ANNOUNCEMENTS_QUERY_KEY,
      });
    },
    displayNotification: true,
  });
};

/**
 * GET /lime-admin/announcements/:announcementId/image/upload-url
 */
export const useGetLimeAdminAnnouncementImageUploadUrl = (
  announcementId: number,
) => {
  return useCustomMutation({
    mutationFn: (query: GetLimeAdminAnnouncementImageUploadUrl["query"]) =>
      get<GetLimeAdminAnnouncementImageUploadUrl>(
        `/lime-admin/announcements/${announcementId}/image/upload-url`,
        query,
      ),
  });
};

/**
 * POST /lime-admin/announcements/:announcementId/image/complete
 */
export const usePostLimeAdminAnnouncementImageComplete = (
  announcementId: number,
) => {
  return useCustomMutation({
    mutationFn: (body: PostLimeAdminAnnouncementImageComplete["body"]) =>
      post<PostLimeAdminAnnouncementImageComplete>(
        `/lime-admin/announcements/${announcementId}/image/complete`,
        body,
      ),
  });
};
